import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/Card';
import DateRangeFilter from '../../components/filters/DateRangeFilter';
import { formatDateYMD } from '../../utils/dateRangeHelpers';
import { Eye, MousePointerClick, ShoppingBag, TrendingUp, AlertTriangle, Lightbulb } from 'lucide-react';

const HIGH5_BRAND_ID = 'b20e13f1-2e3f-4c24-83ce-f2b812bfc3e8';

// Funnel stage mapping. SEARCH lives in MOFU because branded vs. non-branded
// can't be split here without per-keyword data. Unknown types default to BOFU
// (per high5 brand request — assume conversion-oriented unless explicitly upper).
const GOOGLE_TYPE_TO_STAGE = {
  DISPLAY: 'TOFU',
  VIDEO: 'TOFU',
  DISCOVERY: 'TOFU',
  DEMAND_GEN: 'TOFU',
  SEARCH: 'MOFU',
  PERFORMANCE_MAX: 'BOFU',
  SHOPPING: 'BOFU',
  HOTEL: 'BOFU',
  SMART: 'BOFU',
  LOCAL: 'BOFU',
  MULTI_CHANNEL: 'BOFU',
};

const META_OBJECTIVE_TO_STAGE = (objRaw) => {
  const o = String(objRaw || '').toUpperCase();
  if (o.includes('AWARENESS') || o.includes('REACH') || o.includes('VIDEO_VIEW')) return 'TOFU';
  if (o.includes('TRAFFIC') || o.includes('ENGAGEMENT') || o.includes('LINK_CLICK') ||
      o.includes('POST_ENGAGEMENT') || o.includes('LEAD') || o.includes('MESSAGE') ||
      o.includes('APP_INSTALL') || o.includes('APP_PROMOTION')) return 'MOFU';
  // Default → BOFU (covers SALES/CONVERSION/CATALOG/STORE plus anything unknown).
  return 'BOFU';
};

const STAGE_META = {
  TOFU: {
    label: 'TOFU — Awareness',
    sub: 'Reach, Brand Awareness, Video Views, Display, YouTube',
    color: 'blue',
    bg: 'bg-blue-500',
    bgSoft: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    icon: <Eye className="w-5 h-5" />,
  },
  MOFU: {
    label: 'MOFU — Consideration',
    sub: 'Traffic, Engagement, Search, Lead Gen',
    color: 'amber',
    bg: 'bg-amber-500',
    bgSoft: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    icon: <MousePointerClick className="w-5 h-5" />,
  },
  BOFU: {
    label: 'BOFU — Conversion',
    sub: 'Sales, Performance Max, Shopping, Catalog',
    color: 'green',
    bg: 'bg-green-500',
    bgSoft: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
    icon: <ShoppingBag className="w-5 h-5" />,
  },
};

const TR = (n, opts = {}) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0, ...opts });
const TRY_ = (n) => `₺${TR(n)}`;
const PCT = (n) => `${(Number(n) || 0).toFixed(1)}%`;

export default function FunnelPerformance() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState('last30');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [googleRows, setGoogleRows] = useState([]);
  const [metaRows, setMetaRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const brandId = (localStorage.getItem('currentBrandId') || localStorage.getItem('selectedBrandId') || '').trim();
  const isHigh5 = brandId === HIGH5_BRAND_ID;

  useEffect(() => {
    if (!isHigh5) {
      navigate('/');
    }
  }, [isHigh5, navigate]);

  useEffect(() => {
    if (!isHigh5) return;
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const customSuffix = (dateRange === 'custom' && customStart && customEnd)
          ? `&startDate=${formatDateYMD(customStart)}&endDate=${formatDateYMD(customEnd)}`
          : '';
        const headers = { 'X-Brand-ID': brandId };
        const [g, m] = await Promise.all([
          axios.get(`https://api.glowytics.com/api/google-ads/campaign-types-all?dateRange=${dateRange}${customSuffix}`, { headers, withCredentials: true }).catch(() => ({ data: { rawData: [] } })),
          axios.get(`https://api.glowytics.com/api/google-ads/unified-meta-ga4-performance?dateRange=${dateRange}${customSuffix}`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        const gRows = Array.isArray(g.data) ? g.data : (g.data.rawData || g.data.processedData || []);
        const mRows = Array.isArray(m.data) ? m.data : [];
        setGoogleRows(gRows);
        setMetaRows(mRows);
      } catch (e) {
        console.error('Funnel data fetch error:', e);
        setError('Veri çekilirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [isHigh5, brandId, dateRange, customStart, customEnd]);

  const stageData = useMemo(() => {
    const init = () => ({
      stage: '',
      spend: 0, impressions: 0, clicks: 0, sessions: 0, purchases: 0, revenue: 0,
      campaigns: 0, campaignTypes: new Set(),
    });
    const stages = { TOFU: { ...init(), stage: 'TOFU' }, MOFU: { ...init(), stage: 'MOFU' }, BOFU: { ...init(), stage: 'BOFU' } };
    const seenG = new Set();
    googleRows.forEach(r => {
      const type = String(r.campaignType || r.campaign_type || '').toUpperCase();
      const stage = GOOGLE_TYPE_TO_STAGE[type] || 'BOFU';
      const s = stages[stage];
      s.spend += Number(r.googleAdsCost || r.google_ads_cost || 0);
      s.impressions += Number(r.googleAdsImpression || r.google_ads_impression || 0);
      s.clicks += Number(r.googleAdsClicks || r.google_ads_clicks || 0);
      s.sessions += Number(r.sessions || 0);
      s.purchases += Number(r.googleAdsTransaction || r.google_ads_transaction || r.ga4Transaction || r.ga4_transaction || 0);
      s.revenue += Number(r.googleAdsRevenue || r.google_ads_revenue || r.ga4Revenue || r.ga4_revenue || 0);
      const cid = r.campaignId || r.campaign_id;
      if (cid && !seenG.has(`g_${cid}`)) { seenG.add(`g_${cid}`); s.campaigns += 1; }
      if (type) s.campaignTypes.add(type);
    });
    const seenM = new Set();
    metaRows.forEach(r => {
      const stage = META_OBJECTIVE_TO_STAGE(r.campaign_objective || r.campaignObjective);
      const s = stages[stage];
      s.spend += Number(r.spend || 0);
      s.impressions += Number(r.impressions || 0);
      s.clicks += Number(r.clicks || 0);
      s.sessions += Number(r.ga4_sessions || r.ga4Sessions || 0);
      s.purchases += Number(r.ga4_purchases || r.ga4Purchases || r.conversions || 0);
      s.revenue += Number(r.ga4_total_revenue || r.ga4TotalRevenue || r.conversion_value || 0);
      const cid = r.campaign_id || r.campaignId;
      if (cid && !seenM.has(`m_${cid}`)) { seenM.add(`m_${cid}`); s.campaigns += 1; }
      const objRaw = String(r.campaign_objective || r.campaignObjective || '').toUpperCase();
      if (objRaw) s.campaignTypes.add(objRaw);
    });
    Object.values(stages).forEach(s => {
      s.roas = s.spend > 0 ? s.revenue / s.spend : 0;
      s.cpc = s.clicks > 0 ? s.spend / s.clicks : 0;
      s.cpa = s.purchases > 0 ? s.spend / s.purchases : 0;
      s.types = Array.from(s.campaignTypes);
    });
    const total = stages.TOFU.spend + stages.MOFU.spend + stages.BOFU.spend;
    Object.values(stages).forEach(s => { s.sharePct = total > 0 ? (s.spend / total) * 100 : 0; });
    return { stages, totalSpend: total };
  }, [googleRows, metaRows]);

  const recommendation = useMemo(() => {
    const { stages, totalSpend } = stageData;
    if (!totalSpend) return null;
    const tofuShare = stages.TOFU.sharePct;
    const mofuShare = stages.MOFU.sharePct;
    const bofuShare = stages.BOFU.sharePct;
    const messages = [];
    if (bofuShare > 85) {
      messages.push(`Bütçenin **%${bofuShare.toFixed(0)}**'si Conversion (BOFU) odaklı — kısa vadeli ciroyu güzel çeviriyor ama **funnel'ın üstü boş**: yeni müşteri pipeline'ı zayıf.`);
    }
    if (tofuShare < 5) {
      messages.push(`Awareness (TOFU) bütçesi sadece **%${tofuShare.toFixed(1)}**. **YouTube / Display (GDN) / Reach** kampanyalarına ufak bir bütçe ayrılırsa funnel'ın üstü doldurulur, yeni müşteri akışı oluşur.`);
    }
    if (mofuShare < 10) {
      messages.push(`Traffic & Engagement (MOFU) **%${mofuShare.toFixed(1)}**. Düşünme aşamasındaki kullanıcıyı re-target etmek için **Search non-brand** veya **Traffic** objective'leri önerilir.`);
    }
    return { messages };
  }, [stageData]);

  if (!isHigh5) return null;

  const stagesArr = ['TOFU', 'MOFU', 'BOFU'];

  return (
    <div className="p-6 space-y-6 bg-[#f6f8ff]/70 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-2xl shadow-sm">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Funnel Performance</h1>
          <p className="text-sm text-gray-500 mt-1">
            Awareness → Consideration → Conversion: bütçe dağılımınız ve funnel sağlığı
          </p>
        </div>
        <DateRangeFilter
          value={dateRange}
          customStart={customStart}
          customEnd={customEnd}
          onChange={(range, start, end) => {
            setDateRange(range);
            setCustomStart(start);
            setCustomEnd(end);
          }}
          presets={['yesterday', 'last7', 'last30', 'thisMonth', 'lastMonth', 'custom']}
        />
      </div>

      {loading ? (
        <Card className="p-12 text-center">
          <div className="loading-logo-container inline-block">
            <img src="/logoicon.png" alt="Loading" className="loading-logo" />
          </div>
          <p className="text-sm text-gray-500 mt-4">Veriler hazırlanıyor…</p>
        </Card>
      ) : error ? (
        <Card className="p-8 text-center">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-700">{error}</p>
        </Card>
      ) : !stageData.totalSpend ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-gray-500">Seçili tarih aralığında kampanya verisi yok.</p>
        </Card>
      ) : (
        <>
          {/* Funnel visualization — three trapezoid blocks */}
          <Card className="rounded-2xl overflow-hidden bg-white p-6">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Funnel Bütçe Dağılımı</h2>
              <p className="text-sm text-gray-500 mt-1">
                Toplam harcama <span className="font-semibold text-gray-700">{TRY_(stageData.totalSpend)}</span>
                {' '}— funnel stage'lere göre kırılım
              </p>
            </div>

            {/* Stacked horizontal bar */}
            <div className="mb-6">
              <div className="flex w-full h-6 rounded-full overflow-hidden border border-gray-200">
                {stagesArr.map(stage => {
                  const s = stageData.stages[stage];
                  if (s.sharePct < 0.5) return null;
                  return (
                    <div
                      key={stage}
                      className={`${STAGE_META[stage].bg} flex items-center justify-center text-white text-xs font-semibold`}
                      style={{ width: `${s.sharePct}%` }}
                      title={`${STAGE_META[stage].label}: ${PCT(s.sharePct)} (${TRY_(s.spend)})`}
                    >
                      {s.sharePct >= 7 ? PCT(s.sharePct) : ''}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-2 text-[11px] text-gray-500">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>

            {/* Three stage cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {stagesArr.map(stage => {
                const s = stageData.stages[stage];
                const meta = STAGE_META[stage];
                return (
                  <div
                    key={stage}
                    className={`rounded-xl border ${meta.border} ${meta.bgSoft} p-5 relative`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`p-2 rounded-lg ${meta.bg} text-white`}>{meta.icon}</div>
                        <div>
                          <h3 className={`font-semibold ${meta.text}`}>{meta.label}</h3>
                          <p className="text-[11px] text-gray-500 mt-0.5">{meta.sub}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs text-gray-500">Spend</span>
                        <span className="font-semibold text-gray-900">{TRY_(s.spend)}</span>
                      </div>
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs text-gray-500">Bütçe payı</span>
                        <span className={`font-bold text-lg ${meta.text}`}>{PCT(s.sharePct)}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${meta.bg}`} style={{ width: `${Math.min(100, s.sharePct)}%` }} />
                      </div>
                      <div className="grid grid-cols-2 gap-2 pt-2 mt-2 border-t border-gray-200/60 text-xs">
                        <div>
                          <div className="text-gray-500">Impressions</div>
                          <div className="font-medium text-gray-800">{TR(s.impressions)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Clicks</div>
                          <div className="font-medium text-gray-800">{TR(s.clicks)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Sessions</div>
                          <div className="font-medium text-gray-800">{TR(s.sessions)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Purchases</div>
                          <div className="font-medium text-gray-800">{TR(s.purchases)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Revenue</div>
                          <div className="font-medium text-gray-800">{TRY_(s.revenue)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">ROAS</div>
                          <div className="font-medium text-gray-800">{(s.roas || 0).toFixed(2)}x</div>
                        </div>
                      </div>
                      {s.types.length > 0 && (
                        <div className="pt-2 mt-2 border-t border-gray-200/60">
                          <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Kapsamdaki kampanya tipleri</div>
                          <div className="flex flex-wrap gap-1">
                            {s.types.slice(0, 8).map(t => (
                              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-white/70 border border-gray-200 text-gray-600">{t}</span>
                            ))}
                            {s.types.length > 8 && <span className="text-[10px] text-gray-500">+{s.types.length - 8}</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

          </Card>

          {/* Recommendation */}
          {recommendation && recommendation.messages.length > 0 && (
            <Card className="rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 p-6">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-white rounded-xl shadow-sm">
                  <Lightbulb className="w-6 h-6 text-amber-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">Öneriler</h3>
                  <ul className="space-y-3">
                    {recommendation.messages.map((m, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700 leading-relaxed">
                        <TrendingUp className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span dangerouslySetInnerHTML={{ __html: m.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>') }} />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
